import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const TermsOfService = () => {
  return (
    <div className="legal-page-container">
      <div className="legal-content">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: January 2, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Yesod ("the Service"), you accept and agree to be bound by the terms 
            and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Yesod is a comprehensive community management and spiritual practice platform designed to help Jewish communities and individuals organize their religious life. The Service includes:
          </p>
          <p>
            <strong>Community Management:</strong> Tools for synagogues to organize events, manage memberships, facilitate classes, and maintain connections.
          </p>
          <p>
            <strong>Spiritual Tools:</strong> A digital Siddur/Tehillim reader, Hebrew calendar, Zmanim (halachic times), and candle lighting times.
          </p>
          <p>
            <strong>Prayer Management:</strong> Features to create personal prayer lists ("Kvitels"), track reading progress (e.g., Daily or Weekly Tehillim), and collaborate on shared prayer goals.
          </p>
          <p>
            <strong>Social Features:</strong> Functionality to create groups, share prayer lists via links, and track collective community achievements.
          </p>
        </section>

        <section>
          <h2>3. User Accounts and Registration</h2>
          <p>
            To use certain features of the Service (such as saving Prayer Lists), you must register for an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration.</li>
            <li>Maintain and promptly update your account information.</li>
            <li>Maintain the security of your password and account.</li>
            <li>Accept responsibility for all activities under your account.</li>
            <li>Notify us immediately of any unauthorized use of your account.</li>
          </ul>
        </section>

        <section>
          <h2>4. Acceptable Use Policy</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Violate any laws, regulations, or third-party rights.</li>
            <li>Post or transmit harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable content (including within Prayer List titles or Group names).</li>
            <li>Impersonate any person or entity or misrepresent your affiliation.</li>
            <li>Interfere with or disrupt the Service or servers.</li>
            <li>Attempt to gain unauthorized access to any part of the Service.</li>
            <li>Use the Service for any commercial purpose without our express written consent.</li>
            <li>Spam, solicit, or harvest information from other users.</li>
          </ul>
        </section>

        <section>
          <h2>5. Payment Terms and Processing Fees</h2>
          <p>
            <strong>Payment Processing:</strong> All payments are processed through Stripe, our third-party payment processor. 
            By making a payment, you agree to Stripe's Terms of Service.
          </p>
          <p>
            <strong>Processing Fees:</strong> Payment processing fees apply to all transactions made through the platform. 
            These fees cover the cost of secure payment processing and platform maintenance. Fee amounts may vary 
            depending on the payment method and transaction type.
          </p>
          <p>
            <strong>Refunds:</strong> Refund policies are determined by individual synagogue communities. 
            Please contact your community administrator for refund requests.
          </p>
        </section>

        <section>
          <h2>6. WhatsApp Communications</h2>
          <p>
            By providing your phone number, you consent to receive notifications and communications via WhatsApp, including:
          </p>
          <ul>
            <li>Event reminders and community announcements.</li>
            <li>Payment confirmations.</li>
            <li>Prayer alerts or group challenges you have opted into.</li>
            <li>Administrative messages from your synagogue.</li>
          </ul>
          <p>
            You can opt out of WhatsApp communications at any time by updating your communication preferences 
            or contacting us directly. Standard messaging rates may apply.
          </p>
        </section>

        <section>
          <h2>7. SMS Phone Verification and Consent</h2>
          <p>
            <strong>Phone Verification Requirement:</strong> To enhance security and verify your identity, 
            we require phone number verification during account registration and certain account activities.
          </p>
          <p>
            <strong>Account Creation Opt-In:</strong> By creating an account with your mobile phone number, you expressly agree 
            and consent to receive SMS text messages from Yesod. This consent is a condition of account registration and use of the Service.
          </p>
          <p>
            <strong>SMS Consent:</strong> By providing your phone number and requesting verification, you expressly consent to receive 
            SMS text messages from Yesod containing verification codes and security-related communications. These messages include:
          </p>
          <ul>
            <li>One-time verification codes for account verification</li>
            <li>Security alerts and account notifications</li>
            <li>Password reset codes when requested</li>
            <li>Two-factor authentication codes</li>
          </ul>
          <p>
            <strong>Message Frequency and Charges:</strong> You may receive up to 5 SMS messages per verification attempt. 
            Message and data rates may apply according to your mobile carrier's plan. We are not responsible for 
            any charges incurred from your mobile carrier.
          </p>
          <p>
            <strong>Opt-Out:</strong> You can stop receiving SMS verification messages by not completing the verification process, 
            though this may limit your ability to use certain features of the Service. For customer support regarding 
            SMS messages, contact us at shalom@yesod.us.
          </p>
          <p>
            <strong>Carrier Responsibility:</strong> We are not liable for delayed or undelivered messages due to 
            carrier network issues or other factors beyond our control.
          </p>
        </section>

        <section>
          <h2>8. Content and Intellectual Property</h2>
          <p>
            <strong>Your Content (Including Prayer Lists):</strong> You retain ownership of content you submit to the Service, including names added to Prayer Lists, Group names, and personal notes. By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, display, store, and distribute your content in connection with the Service (e.g., to allow your shared lists to be viewed by recipients).
          </p>
          <p>
            <strong>Third-Party Names:</strong> By adding names of third parties to your Prayer Lists (e.g., for Refuah or memory), you represent that you have a benevolent intent and, where necessary, the permission to use such information for religious purposes. You agree not to use the Prayer List feature to harass or publicly expose private individuals without cause.
          </p>
          <p>
            <strong>Our Content:</strong> The Service and its original content (including the app design, code, and proprietary algorithms), features, and functionality 
            are owned by Yesod and are protected by copyright, trademark, and other intellectual property laws.
          </p>
        </section>

        <section>
          <h2>9. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information. 
            By using the Service, you agree to the collection and use of information in accordance with our Privacy Policy.
          </p>
        </section>

        <section>
          <h2>10. Community Guidelines</h2>
          <p>
            Yesod is designed to foster positive community engagement. Users are expected to:
          </p>
          <ul>
            <li>Treat all community members with respect and dignity</li>
            <li>Use appropriate language and behavior</li>
            <li>Respect the religious and cultural values of the community</li>
            <li>Follow the specific guidelines set by their synagogue community</li>
            <li>Report inappropriate behavior or content to administrators</li>
          </ul>
        </section>

        <section>
          <h2>11. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice, 
            if you breach these Terms of Service. Upon termination, your right to use the Service will cease immediately.
          </p>
          <p>
            You may also terminate your account at any time by contacting us or using the account deletion 
            feature in your account settings.
          </p>
        </section>

        <section>
          <h2>12. Disclaimers and Limitation of Liability</h2>
          <p>
            <strong>Service "As Is":</strong> The Service is provided on an "as is" and "as available" basis. 
            We make no warranties, expressed or implied, regarding the Service.
          </p>
          <p>
            <strong>Religious Practice Disclaimer:</strong> Yesod provides digital tools to facilitate prayer and religious observance. We do not guarantee the textual accuracy of religious texts (Tehillim, Siddur) compared to physical prints, nor do we guarantee any specific spiritual or religious outcomes resulting from the use of the App.
          </p>
          <p>
            <strong>Limitation of Liability:</strong> To the maximum extent permitted by law, Yesod shall not 
            be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.
          </p>
        </section>

        <section>
          <h2>13. Third-Party Services</h2>
          <p>
            The Service may contain links to third-party websites or services (such as Stripe for payments and WhatsApp for messaging). 
            We are not responsible for the content, privacy policies, or practices of any third-party services.
          </p>
        </section>

        <section>
          <h2>14. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms of Service at any time. We will notify users of any material 
            changes by posting the new terms on this page and updating the "Last updated" date. 
            Your continued use of the Service after changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2>15. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of Florida, United States, 
            without regard to its conflict of law provisions.
          </p>
        </section>

        <section>
          <h2>16. Third-Party Content and Attribution</h2>
          <p>
            <strong>English Translation of Psalms:</strong> The Rashi Ketuvim by Rabbi Shraga Silverstein is used 
            under the CC-BY 4.0 International License. Source: <a href="https://www.sefaria.org" target="_blank" rel="noopener noreferrer">Sefaria.org</a>.
          </p>
        </section>

        <section>
          <h2>17. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <p>
            Email: shalom@yesod.us
          </p>
          <p>
            WhatsApp Support: <a href="https://wa.me/message/CUTHC4T6KYIOL1" target="_blank" rel="noopener noreferrer">Contact us on WhatsApp</a>
          </p>
        </section>

        <section>
          <h2>18. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be 
            limited or eliminated to the minimum extent necessary so that the Terms will otherwise remain in full force and effect.
          </p>
        </section>

        <div className="back-to-app">
          <Link to="/" className="back-link">← Back to Yesod</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;